/**
 * Sanitize user-provided file names for Firebase Storage object paths.
 * Characters like `{}` `[]` can break Storage rules path matching (wildcard syntax);
 * `#` `/` `\` fragment or segment paths unexpectedly.
 */
export function sanitizeFileNameForStoragePath(fileName: string, maxLen = 200): string {
  const s = fileName.replace(/[^a-zA-Z0-9.-]/g, '_').trim().slice(0, maxLen);
  return s || 'file';
}

/** Extension only (with dot), alphanumeric — avoids long or odd paths under Storage rules `**` captures. */
export function safeStorageObjectExtension(fileName: string): string {
  const i = fileName.lastIndexOf('.');
  if (i <= 0 || i >= fileName.length - 1) return '';
  const raw = fileName.slice(i + 1);
  const cleaned = raw.replace(/[^a-zA-Z0-9]/g, '').slice(0, 16).toLowerCase();
  return cleaned ? `.${cleaned}` : '';
}

/**
 * Storage object file name: unique id, then the sanitized original name after `~`.
 * Viewers rebuild a title from the storage path, so the original basename has to
 * live here. `~` is not kept by {@link sanitizeFileNameForStoragePath}, so it
 * stays a delimiter and never appears inside the human name.
 */
export function buildCommentAttachmentStorageFileName(fileId: string, originalFileName: string): string {
  const base = (originalFileName || '').split(/[/\\]/).pop() || 'file';
  const ext = safeStorageObjectExtension(base);
  const human = sanitizeFileNameForStoragePath(base, 120);
  const labeled = human.includes('.') ? human : `${human}${ext}`;
  return `${fileId}~${labeled}`;
}
